import { ISelectMenuItemData } from "@/app/components/ui/interface";
import {
  IVoucherCategoryWithSubCategoriesData,
  IVoucherSubCategoryData,
} from "@/app/hooks/api_hooks/Membership/useVoucherCategorySubCategoryAPI";

/**
 * Single source of truth for the voucher setup category/sub category dropdowns.
 * `/clinic-org/vms/voucher/category-subcategory/` is fetched once on page load and both
 * dropdowns are derived from that response, so no sub category call is needed on selection.
 */
export interface IVoucherCategorySubCategoryMap {
  /** Categories ready to be passed to a select menu. */
  categorySelectItems: ISelectMenuItemData[];
  /** Sub category select items keyed by their parent category id. */
  subCategorySelectItemsByCategoryId: Record<string, ISelectMenuItemData[]>;
}

const toSelectItem = (id: string, title: string, state: ISelectMenuItemData["state"] = {}): ISelectMenuItemData => ({
  title: title ?? "",
  id: id ?? "",
  checkboxVisible: true,
  withSubItems: false,
  subSelectItems: [],
  state,
});

const mapSubCategory = (subCategory: IVoucherSubCategoryData): ISelectMenuItemData =>
  toSelectItem(subCategory?.subcategory_id, subCategory?.subcategory_name, {
    is_medicine: subCategory?.is_medicine ?? false,
  });

export const mapVoucherCategorySubCategory = (
  data: IVoucherCategoryWithSubCategoriesData[] | undefined,
): IVoucherCategorySubCategoryMap => {
  if (!Array.isArray(data)) {
    return { categorySelectItems: [], subCategorySelectItemsByCategoryId: {} };
  }

  return data.reduce<IVoucherCategorySubCategoryMap>(
    (final, category) => {
      if (!category?.id) return final;

      final.categorySelectItems.push(
        toSelectItem(category.id, category.category_name, { clinic_or_tams: category.clinic_or_tams ?? null }),
      );
      final.subCategorySelectItemsByCategoryId[category.id] = Array.isArray(category.subcategories)
        ? category.subcategories.filter((subCategory) => !!subCategory?.subcategory_id).map(mapSubCategory)
        : [];

      return final;
    },
    { categorySelectItems: [], subCategorySelectItemsByCategoryId: {} },
  );
};

export const getVoucherSubCategoryItems = (
  map: IVoucherCategorySubCategoryMap,
  categoryId: string,
): ISelectMenuItemData[] => (categoryId ? (map?.subCategorySelectItemsByCategoryId?.[categoryId] ?? []) : []);
